import { palettes } from '../config';
import { makeFallbackWaveform } from '../media/audio';
import type { AspectId, AudioMeta, ImageCrop, ImagePlacement, PaletteId, TemplateId, UploadedImage } from '../types';

type Palette = (typeof palettes)[PaletteId];

const editorialMetaFamily = '"Courier New", Courier, ui-monospace, monospace';
const imageTextColor = '#fffaf2';

type TemplateTypography = {
  titleFamily: string;
  titleWeight: string;
  subtitleFamily: string;
  subtitleWeight: string;
  mutedAlpha: number;
};

const templateTypography: Record<TemplateId, TemplateTypography> = {
  corporate: {
    titleFamily: 'Aptos, "Segoe UI Variable", "Segoe UI", ui-sans-serif, system-ui, sans-serif',
    titleWeight: '700',
    subtitleFamily: '"Segoe UI", ui-sans-serif, system-ui, sans-serif',
    subtitleWeight: '500',
    mutedAlpha: 0.78,
  },
  editorial: {
    titleFamily: 'Georgia, "Times New Roman", serif',
    titleWeight: '700',
    subtitleFamily: 'Palatino, "Palatino Linotype", Georgia, serif',
    subtitleWeight: '500',
    mutedAlpha: 0.72,
  },
  podcast: {
    titleFamily: '"Arial Rounded MT Bold", "Avenir Next", "Trebuchet MS", ui-sans-serif, system-ui, sans-serif',
    titleWeight: '800',
    subtitleFamily: '"Avenir Next", "Trebuchet MS", ui-sans-serif, system-ui, sans-serif',
    subtitleWeight: '600',
    mutedAlpha: 0.78,
  },
  minimal: {
    titleFamily: '"Helvetica Neue", Helvetica, Arial, ui-sans-serif, system-ui, sans-serif',
    titleWeight: '600',
    subtitleFamily: '"Helvetica Neue", Helvetica, Arial, ui-sans-serif, system-ui, sans-serif',
    subtitleWeight: '400',
    mutedAlpha: 0.7,
  },
  bold: {
    titleFamily: 'Impact, "Arial Black", "Arial Narrow Bold", Haettenschweiler, ui-sans-serif, system-ui, sans-serif',
    titleWeight: '900',
    subtitleFamily: '"Arial Black", Impact, ui-sans-serif, system-ui, sans-serif',
    subtitleWeight: '700',
    mutedAlpha: 0.76,
  },
};

type TitleCardOptions = {
  aspect: AspectId;
  audioMeta: AudioMeta | null;
  image: UploadedImage | null;
  imagePlacement: ImagePlacement;
  subtitle: string;
  template: TemplateId;
  title: string;
};

type TitleCardContext = {
  context: CanvasRenderingContext2D;
  width: number;
  height: number;
  margin: number;
  palette: Palette;
  aspect: AspectId;
  waveform: number[];
  title: string;
  subtitle: string;
  image: UploadedImage | null;
  imagePlacement: ImagePlacement;
};

const templateRenderers: Record<TemplateId, (card: TitleCardContext) => void> = {
  bold: drawBoldTemplate,
  corporate: drawCorporateTemplate,
  editorial: drawEditorialTemplate,
  minimal: drawMinimalTemplate,
  podcast: drawPodcastTemplate,
};

const imageBackgroundRenderers: Record<TemplateId, (card: TitleCardContext, image: UploadedImage) => void> = {
  bold: drawBoldImageBackground,
  corporate: drawCorporateImageBackground,
  editorial: drawEditorialImageBackground,
  minimal: drawMinimalImageBackground,
  podcast: drawPodcastImageBackground,
};

export function drawTitleCard(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  palette: Palette,
  options: TitleCardOptions,
) {
  const card: TitleCardContext = {
    context,
    width,
    height,
    margin: Math.round(Math.min(width, height) * 0.08),
    palette,
    aspect: options.aspect,
    waveform: options.audioMeta?.waveform ?? makeFallbackWaveform(96),
    title: options.title || 'Untitled audio',
    subtitle: options.subtitle.trim(),
    image: options.image,
    imagePlacement: options.imagePlacement,
  };

  drawTemplateBackground(card, options.template);
  (templateRenderers[options.template] ?? templateRenderers.corporate)(card);
}

function drawTemplateBackground(card: TitleCardContext, template: TemplateId) {
  const { context, width, height, palette } = card;

  if (card.image) {
    (imageBackgroundRenderers[template] ?? imageBackgroundRenderers.corporate)(card, card.image);
    return;
  }

  if (template === 'minimal') {
    context.fillStyle = palette.background;
    context.fillRect(0, 0, width, height);
    return;
  }

  if (template === 'bold') {
    context.fillStyle = palette.background;
    context.fillRect(0, 0, width, height);
    context.fillStyle = palette.accent;
    context.beginPath();
    context.moveTo(width * 0.58, 0);
    context.lineTo(width, 0);
    context.lineTo(width, height);
    context.lineTo(width * 0.34, height);
    context.closePath();
    context.fill();
    context.fillStyle = 'rgba(0,0,0,0.16)';
    context.fillRect(0, 0, width, height);
    return;
  }

  const gradient = context.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, palette.background);
  gradient.addColorStop(template === 'editorial' ? 0.42 : 0.58, palette.background);
  gradient.addColorStop(1, mixColors(palette.background, template === 'podcast' ? palette.accent : palette.accentTwo, template === 'podcast' ? 0.52 : 0.38));
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);
}

function fillDimmedBase(card: TitleCardContext, lightWeight: number, darkWeight: number) {
  const { context, width, height, palette } = card;
  context.fillStyle = mixColors(palette.background, '#111214', palette.isLight ? lightWeight : darkWeight);
  context.fillRect(0, 0, width, height);
}

function drawMinimalImageBackground(card: TitleCardContext, image: UploadedImage) {
  const { context, width, height, margin, palette, imagePlacement } = card;
  fillDimmedBase(card, 0.78, 0.32);

  const imageSize = Math.min(width * 0.36, height * 0.36);
  const imageX = width * 0.5 - imageSize * 0.5;
  const imageY = height * 0.23;
  context.globalAlpha = 0.28;
  withRoundedClip(context, imageX, imageY, imageSize, imageSize, Math.max(18, imageSize * 0.08), () => {
    drawPlacedImage(context, image.element, imageX, imageY, imageSize, imageSize, image.crop, {
      ...imagePlacement,
      fit: 'contain',
      zoom: Math.max(0.5, imagePlacement.zoom * 0.9),
    });
  });
  context.globalAlpha = 1;

  context.strokeStyle = colorWithAlpha(palette.accent, 0.48);
  context.lineWidth = Math.max(2, height * 0.004);
  context.beginPath();
  context.moveTo(margin, height - margin);
  context.lineTo(width - margin, height - margin);
  context.stroke();
}

function drawCorporateImageBackground(card: TitleCardContext, image: UploadedImage) {
  const { context, width, height, margin, palette, aspect, imagePlacement } = card;
  fillDimmedBase(card, 0.82, 0.42);
  drawGrid(context, width, height, 'rgba(255,255,255,0.065)');

  const cardX = aspect === 'story' ? margin : width * 0.62;
  const cardY = aspect === 'story' ? margin * 1.15 : margin * 1.3;
  const cardWidth = aspect === 'story' ? width - margin * 2 : width - cardX - margin;
  const cardHeight = aspect === 'story' ? height * 0.26 : height - cardY - margin * 1.45;
  drawImageCard(context, image, cardX, cardY, cardWidth, cardHeight, imagePlacement, Math.max(14, margin * 0.18), {
    matte: 'rgba(255,255,255,0.92)',
    border: colorWithAlpha(palette.accent, 0.75),
    borderWidth: Math.max(3, height * 0.006),
    shadow: 'rgba(0,0,0,0.26)',
  });

  context.fillStyle = palette.accent;
  context.fillRect(0, 0, Math.max(14, width * 0.018), height);
  context.fillStyle = colorWithAlpha(palette.accentTwo, 0.6);
  context.fillRect(width - Math.max(14, width * 0.018), 0, Math.max(14, width * 0.018), height);
}

function drawEditorialImageBackground(card: TitleCardContext, image: UploadedImage) {
  const { context, width, height, margin, aspect, imagePlacement } = card;
  fillDimmedBase(card, 0.8, 0.36);

  const photoWidth = aspect === 'story' ? width - margin * 2 : width * 0.42;
  const photoHeight = aspect === 'story' ? height * 0.34 : height * 0.56;
  const photoX = aspect === 'story' ? margin : width - margin - photoWidth;
  const photoY = aspect === 'story' ? height - margin * 1.45 - photoHeight : height * 0.26;
  drawImageCard(context, image, photoX, photoY, photoWidth, photoHeight, imagePlacement, 4, {
    matte: 'rgba(255,250,242,0.88)',
    border: 'rgba(255,250,242,0.76)',
    borderWidth: Math.max(10, margin * 0.16),
    shadow: 'rgba(0,0,0,0.3)',
  });

  const wash = context.createLinearGradient(0, 0, width, height);
  wash.addColorStop(0, 'rgba(8,9,10,0.5)');
  wash.addColorStop(0.56, 'rgba(8,9,10,0.2)');
  wash.addColorStop(1, 'rgba(8,9,10,0.58)');
  context.fillStyle = wash;
  context.fillRect(0, 0, width, height);
}

function drawPodcastImageBackground(card: TitleCardContext, image: UploadedImage) {
  const { context, width, height, margin, palette, aspect, imagePlacement } = card;
  const gradient = context.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, mixColors(palette.background, '#111214', palette.isLight ? 0.86 : 0.48));
  gradient.addColorStop(0.58, mixColors(palette.background, palette.accent, 0.28));
  gradient.addColorStop(1, mixColors('#111214', palette.accentTwo, 0.22));
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);

  const coverSize = aspect === 'story' ? Math.min(width * 0.66, height * 0.27) : Math.min(height * 0.6, width * 0.34);
  const coverX = aspect === 'story' ? width * 0.5 - coverSize * 0.5 : width - margin - coverSize;
  const coverY = aspect === 'story' ? height * 0.11 : height * 0.2;
  drawPodcastHalo(context, coverX + coverSize * 0.5, coverY + coverSize * 0.5, coverSize, palette);
  drawImageCard(context, image, coverX, coverY, coverSize, coverSize, imagePlacement, Math.max(18, coverSize * 0.08), {
    matte: 'rgba(255,255,255,0.12)',
    border: colorWithAlpha(palette.accent, 0.82),
    borderWidth: Math.max(4, coverSize * 0.018),
    shadow: 'rgba(0,0,0,0.38)',
  });
}

function drawBoldImageBackground(card: TitleCardContext, image: UploadedImage) {
  const { context, width, height, palette, imagePlacement } = card;
  context.fillStyle = palette.background;
  context.fillRect(0, 0, width, height);
  drawPlacedImage(context, image.element, 0, 0, width, height, image.crop, imagePlacement);

  context.save();
  context.globalCompositeOperation = 'multiply';
  context.fillStyle = colorWithAlpha(palette.accent, 0.34);
  context.fillRect(0, 0, width, height);
  context.restore();

  const shadow = context.createLinearGradient(0, 0, width, 0);
  shadow.addColorStop(0, 'rgba(8,9,10,0.96)');
  shadow.addColorStop(0.48, 'rgba(8,9,10,0.72)');
  shadow.addColorStop(1, 'rgba(8,9,10,0.28)');
  context.fillStyle = shadow;
  context.fillRect(0, 0, width, height);

  context.fillStyle = colorWithAlpha(palette.accentTwo, 0.78);
  context.beginPath();
  context.moveTo(width * 0.72, 0);
  context.lineTo(width, 0);
  context.lineTo(width, height);
  context.lineTo(width * 0.48, height);
  context.closePath();
  context.fill();

  context.fillStyle = 'rgba(8,9,10,0.36)';
  context.beginPath();
  context.moveTo(width * 0.62, 0);
  context.lineTo(width * 0.74, 0);
  context.lineTo(width * 0.5, height);
  context.lineTo(width * 0.38, height);
  context.closePath();
  context.fill();
}

function templateTextColors(palette: Palette, hasImage: boolean, mutedAlpha: number) {
  return {
    textColor: hasImage ? imageTextColor : palette.text,
    mutedColor: hasImage ? `rgba(255,250,242,${mutedAlpha})` : palette.muted,
  };
}

function drawCorporateTemplate(card: TitleCardContext) {
  const { context, width, height, margin, palette, aspect, waveform, title, subtitle } = card;
  const hasImage = card.image !== null;
  const typography = templateTypography.corporate;
  const { textColor, mutedColor } = templateTextColors(palette, hasImage, typography.mutedAlpha);
  drawGrid(context, width, height, hasImage ? 'rgba(255,255,255,0.075)' : palette.isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)');

  context.fillStyle = palette.accent;
  context.fillRect(0, 0, Math.max(14, width * 0.018), height);

  if (!hasImage) {
    context.fillStyle = palette.accentTwo;
    context.globalAlpha = 0.86;
    context.fillRect(width - width * 0.18, 0, width * 0.18, height);
    context.globalAlpha = 1;
  }

  const textWidth = hasImage && aspect !== 'story' ? width * 0.5 : width - margin * 2 - Math.max(20, width * 0.08);
  const titleTop = hasImage && aspect === 'story' ? height * (subtitle ? 0.45 : 0.5) : height * (aspect === 'story' ? (subtitle ? 0.33 : 0.4) : subtitle ? 0.34 : 0.4);
  drawTextBlock(context, {
    title,
    subtitle,
    x: margin,
    width: textWidth,
    typography,
    titleColor: textColor,
    titleMaxSize: height * 0.15,
    titleMinSize: height * 0.055,
    titleMaxLines: 3,
    titleLineHeight: 1.08,
    titleTop,
    subtitleColor: mutedColor,
    subtitleSize: Math.max(22, height * 0.04),
    subtitleWidth: textWidth,
    subtitleGap: height * 0.045,
    subtitleLineHeight: 1.32,
  });

  drawWaveform(context, waveform, margin, height - margin * 1.9, width - margin * 2, height * 0.15, palette);
}

function drawEditorialTemplate(card: TitleCardContext) {
  const { context, width, height, margin, palette, waveform, title, subtitle } = card;
  const hasImage = card.image !== null;
  const typography = templateTypography.editorial;
  const { textColor, mutedColor } = templateTextColors(palette, hasImage, typography.mutedAlpha);
  const textWidth = hasImage && width > height ? width * 0.5 : width - margin * 2;

  context.strokeStyle = hasImage ? 'rgba(255,250,242,0.46)' : palette.accent;
  context.lineWidth = Math.max(3, height * 0.006);
  context.beginPath();
  context.moveTo(margin, margin * 1.1);
  context.lineTo(width - margin, margin * 1.1);
  context.stroke();

  context.font = `800 ${Math.max(18, height * 0.026)}px ${editorialMetaFamily}`;
  context.fillStyle = mutedColor;
  context.textBaseline = 'alphabetic';
  context.fillText('AUDIO BRIEFING', margin, margin * 1.75);

  drawTextBlock(context, {
    title,
    subtitle,
    x: margin,
    width: textWidth,
    typography,
    titleColor: textColor,
    titleMaxSize: height * 0.135,
    titleMinSize: height * 0.052,
    titleMaxLines: 4,
    titleLineHeight: 1.02,
    titleTop: height * 0.32,
    subtitleColor: mutedColor,
    subtitleSize: Math.max(20, height * 0.037),
    subtitleWidth: textWidth * 0.78,
    subtitleGap: height * 0.05,
    subtitleLineHeight: 1.35,
  });

  drawWaveform(context, waveform, margin, height - margin * 1.35, width - margin * 2, height * 0.07, palette);
}

function drawPodcastTemplate(card: TitleCardContext) {
  const { context, width, height, margin, palette, aspect, waveform, title, subtitle } = card;
  const hasImage = card.image !== null;
  const typography = templateTypography.podcast;
  const { textColor, mutedColor } = templateTextColors(palette, hasImage, typography.mutedAlpha);

  if (!hasImage) {
    const circleRadius = Math.min(width, height) * (aspect === 'story' ? 0.28 : 0.34);
    const circleX = aspect === 'story' ? width * 0.5 : width * 0.76;
    const circleY = aspect === 'story' ? height * 0.27 : height * 0.44;

    context.fillStyle = palette.accent;
    context.globalAlpha = 0.92;
    context.beginPath();
    context.arc(circleX, circleY, circleRadius, 0, Math.PI * 2);
    context.fill();
    context.globalAlpha = 1;

    context.strokeStyle = palette.accentTwo;
    context.lineWidth = Math.max(8, Math.min(width, height) * 0.018);
    context.beginPath();
    context.arc(circleX, circleY, circleRadius * 0.72, 0, Math.PI * 2);
    context.stroke();
  }

  const textWidth = aspect === 'story' ? width - margin * 2 : hasImage ? width * 0.52 : width * 0.58;
  drawTextBlock(context, {
    title,
    subtitle,
    x: margin,
    width: textWidth,
    typography,
    titleColor: textColor,
    titleMaxSize: height * 0.13,
    titleMinSize: height * 0.052,
    titleMaxLines: 3,
    titleLineHeight: 1.05,
    titleTop: aspect === 'story' ? height * 0.48 : height * 0.27,
    subtitleColor: mutedColor,
    subtitleSize: Math.max(22, height * 0.04),
    subtitleWidth: textWidth,
    subtitleGap: height * 0.045,
    subtitleLineHeight: 1.28,
  });

  drawWaveform(context, waveform, margin, height - margin * 1.75, width - margin * 2, height * 0.13, palette);
}

function drawMinimalTemplate(card: TitleCardContext) {
  const { context, width, height, margin, palette, waveform, title, subtitle } = card;
  const hasImage = card.image !== null;
  const typography = templateTypography.minimal;
  const { textColor, mutedColor } = templateTextColors(palette, hasImage, typography.mutedAlpha);
  const textWidth = width - margin * 2.4;

  context.fillStyle = palette.accent;
  context.globalAlpha = hasImage ? 0.9 : 1;
  context.fillRect(width * 0.5 - Math.max(34, width * 0.035), margin, Math.max(68, width * 0.07), Math.max(5, height * 0.008));
  context.globalAlpha = 1;

  drawTextBlock(context, {
    title,
    subtitle,
    x: width * 0.5,
    width: textWidth,
    align: 'center',
    typography,
    titleColor: textColor,
    titleMaxSize: height * 0.105,
    titleMinSize: height * 0.048,
    titleMaxLines: 3,
    titleLineHeight: 1.12,
    titleTop: (titleHeight) => height * 0.5 - (titleHeight + (subtitle ? height * 0.08 : 0)) * 0.5,
    subtitleColor: mutedColor,
    subtitleSize: Math.max(18, height * 0.033),
    subtitleWidth: textWidth * 0.82,
    subtitleGap: height * 0.04,
    subtitleLineHeight: 1.32,
  });

  drawWaveform(context, waveform, margin * 1.2, height - margin * 1.35, width - margin * 2.4, height * 0.055, palette);
}

function drawBoldTemplate(card: TitleCardContext) {
  const { context, width, height, margin, palette, aspect, waveform, title, subtitle } = card;
  const hasImage = card.image !== null;
  const typography = templateTypography.bold;
  const { textColor, mutedColor } = templateTextColors(palette, hasImage, typography.mutedAlpha);
  const textWidth = aspect === 'story' ? width - margin * 2 : width * 0.74;

  context.fillStyle = palette.accentTwo;
  context.globalAlpha = 0.96;
  context.fillRect(margin, margin, Math.max(80, width * 0.13), Math.max(10, height * 0.018));
  context.globalAlpha = 1;

  drawTextBlock(context, {
    title,
    subtitle,
    x: margin,
    width: textWidth,
    typography,
    titleColor: textColor,
    titleMaxSize: height * 0.18,
    titleMinSize: height * 0.065,
    titleMaxLines: aspect === 'story' ? 4 : 3,
    titleLineHeight: 0.98,
    titleTop: aspect === 'story' ? height * 0.28 : height * 0.3,
    subtitleColor: mutedColor,
    subtitleSize: Math.max(21, height * 0.04),
    subtitleWidth: textWidth * 0.75,
    subtitleGap: height * 0.048,
    subtitleLineHeight: 1.2,
  });

  drawWaveform(context, waveform, margin, height - margin * 1.7, width - margin * 2, height * 0.16, palette);
}

type TextBlockSpec = {
  title: string;
  subtitle: string;
  x: number;
  width: number;
  align?: 'left' | 'center';
  typography: TemplateTypography;
  titleColor: string;
  titleMaxSize: number;
  titleMinSize: number;
  titleMaxLines: number;
  titleLineHeight: number;
  titleTop: number | ((titleHeight: number) => number);
  subtitleColor: string;
  subtitleSize: number;
  subtitleWidth: number;
  subtitleGap: number;
  subtitleLineHeight: number;
};

function drawTextBlock(context: CanvasRenderingContext2D, spec: TextBlockSpec) {
  const { titleFamily, titleWeight, subtitleFamily, subtitleWeight } = spec.typography;
  const { size, lines } = fitTitle(context, spec.title, spec.width, spec.titleMaxSize, spec.titleMinSize, spec.titleMaxLines, titleWeight, titleFamily);
  const titleHeight = lines.length * size * spec.titleLineHeight;
  const titleTop = typeof spec.titleTop === 'function' ? spec.titleTop(titleHeight) : spec.titleTop;
  const draw = spec.align === 'center' ? drawLinesCentered : drawLines;

  context.textBaseline = 'alphabetic';
  context.font = `${titleWeight} ${size}px ${titleFamily}`;
  context.fillStyle = spec.titleColor;
  draw(context, lines, spec.x, titleTop, size * spec.titleLineHeight);

  if (spec.subtitle) {
    context.font = `${subtitleWeight} ${spec.subtitleSize}px ${subtitleFamily}`;
    context.fillStyle = spec.subtitleColor;
    draw(
      context,
      wrapText(context, spec.subtitle, spec.subtitleWidth).slice(0, 2),
      spec.x,
      titleTop + titleHeight + spec.subtitleGap,
      spec.subtitleSize * spec.subtitleLineHeight,
    );
  }
}

type ImageCardStyle = {
  matte: string;
  border: string;
  borderWidth: number;
  shadow: string;
};

function drawImageCard(
  context: CanvasRenderingContext2D,
  image: UploadedImage,
  x: number,
  y: number,
  width: number,
  height: number,
  placement: ImagePlacement,
  radius: number,
  style: ImageCardStyle,
) {
  if (width <= 0 || height <= 0) {
    return;
  }

  context.save();
  context.shadowColor = style.shadow;
  context.shadowBlur = Math.max(16, Math.min(width, height) * 0.08);
  context.shadowOffsetY = Math.max(8, Math.min(width, height) * 0.03);
  drawRoundedRect(context, x, y, width, height, radius, style.matte);
  context.restore();

  const inset = Math.max(0, Math.min(style.borderWidth, width * 0.16, height * 0.16));
  const innerX = x + inset;
  const innerY = y + inset;
  const innerWidth = width - inset * 2;
  const innerHeight = height - inset * 2;
  const innerRadius = Math.max(2, radius - inset * 0.7);

  withRoundedClip(context, innerX, innerY, innerWidth, innerHeight, innerRadius, () => {
    drawPlacedImage(context, image.element, innerX, innerY, innerWidth, innerHeight, image.crop, placement);
  });

  drawRoundedStroke(context, x, y, width, height, radius, style.border, Math.max(1, style.borderWidth * 0.42));
}

function drawPodcastHalo(context: CanvasRenderingContext2D, centerX: number, centerY: number, size: number, palette: Palette) {
  context.save();
  context.lineWidth = Math.max(3, size * 0.012);
  [0.64, 0.84, 1.04].forEach((scale, index) => {
    context.strokeStyle = index === 1 ? colorWithAlpha(palette.accentTwo, 0.38) : colorWithAlpha(palette.accent, 0.3);
    context.beginPath();
    context.arc(centerX, centerY, size * scale * 0.5, 0, Math.PI * 2);
    context.stroke();
  });
  context.fillStyle = colorWithAlpha(palette.accent, 0.2);
  context.beginPath();
  context.arc(centerX + size * 0.28, centerY - size * 0.3, size * 0.12, 0, Math.PI * 2);
  context.fill();
  context.restore();
}

function drawPlacedImage(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
  crop: ImageCrop | null,
  placement: ImagePlacement,
) {
  const imageWidth = image.naturalWidth || image.width;
  const imageHeight = image.naturalHeight || image.height;
  if (!imageWidth || !imageHeight || !width || !height) {
    return;
  }

  const cropX = crop?.x ?? 0;
  const cropY = crop?.y ?? 0;
  const cropWidth = crop?.width ?? imageWidth;
  const cropHeight = crop?.height ?? imageHeight;
  if (!cropWidth || !cropHeight) {
    return;
  }

  const baseScale = placement.fit === 'contain' ? Math.min(width / cropWidth, height / cropHeight) : Math.max(width / cropWidth, height / cropHeight);
  const scale = baseScale * clamp(placement.zoom, 0.5, 2.5);
  const destinationWidth = cropWidth * scale;
  const destinationHeight = cropHeight * scale;
  const focalX = clamp(placement.focalPoint.x, 0, 1);
  const focalY = clamp(placement.focalPoint.y, 0, 1);
  const destinationX = clampImageOffset(x + width * focalX - destinationWidth * focalX, x, width, destinationWidth);
  const destinationY = clampImageOffset(y + height * focalY - destinationHeight * focalY, y, height, destinationHeight);

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, cropX, cropY, cropWidth, cropHeight, destinationX, destinationY, destinationWidth, destinationHeight);
}

function withRoundedClip(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  draw: () => void,
) {
  context.save();
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
  context.clip();
  draw();
  context.restore();
}

function drawRoundedStroke(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number, stroke: string, lineWidth: number) {
  context.save();
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
  context.strokeStyle = stroke;
  context.lineWidth = lineWidth;
  context.stroke();
  context.restore();
}

function clampImageOffset(proposedOffset: number, frameStart: number, frameSize: number, imageSize: number) {
  const minOffset = imageSize >= frameSize ? frameStart + frameSize - imageSize : frameStart;
  const maxOffset = imageSize >= frameSize ? frameStart : frameStart + frameSize - imageSize;
  return clamp(proposedOffset, minOffset, maxOffset);
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function drawGrid(context: CanvasRenderingContext2D, width: number, height: number, color: string) {
  context.strokeStyle = color;
  context.lineWidth = 1;
  const gap = Math.max(56, Math.round(Math.min(width, height) / 10));
  context.beginPath();
  for (let x = 0; x <= width; x += gap) {
    context.moveTo(x, 0);
    context.lineTo(x, height);
  }
  for (let y = 0; y <= height; y += gap) {
    context.moveTo(0, y);
    context.lineTo(width, y);
  }
  context.stroke();
}

function drawWaveform(
  context: CanvasRenderingContext2D,
  samples: number[],
  x: number,
  y: number,
  width: number,
  height: number,
  palette: Palette,
) {
  const barGap = Math.max(3, width / samples.length / 4);
  const barWidth = width / samples.length - barGap;
  const centerY = y + height / 2;

  samples.forEach((sample, index) => {
    const barHeight = Math.max(4, sample * height);
    const barX = x + index * (barWidth + barGap);
    drawRoundedRect(context, barX, centerY - barHeight / 2, barWidth, barHeight, barWidth / 2, index % 3 === 0 ? palette.accentTwo : palette.accent);
  });
}

function fitTitle(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxSize: number,
  minSize: number,
  maxLines: number,
  weight: string,
  family: string,
) {
  const wrapAt = (size: number) => {
    context.font = `${weight} ${size}px ${family}`;
    return wrapText(context, text, maxWidth);
  };

  // Candidate sizes descend from maxSize in steps of 2; binary-search the
  // largest size whose wrap fits within maxLines.
  const sizeCount = maxSize >= minSize ? Math.floor((maxSize - minSize) / 2) + 1 : 0;
  let low = 0;
  let high = sizeCount - 1;
  let best: { size: number; lines: string[] } | null = null;
  while (low <= high) {
    const mid = (low + high) >> 1;
    const size = maxSize - mid * 2;
    const lines = wrapAt(size);
    if (lines.length <= maxLines) {
      best = { size, lines };
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }

  return best ?? { size: minSize, lines: wrapAt(minSize).slice(0, maxLines) };
}

function wrapText(context: CanvasRenderingContext2D, text: string, maxWidth: number) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';

  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (context.measureText(candidate).width <= maxWidth) {
      line = candidate;
      continue;
    }
    if (line) {
      lines.push(line);
    }
    if (context.measureText(word).width > maxWidth) {
      lines.push(...breakLongWord(context, word, maxWidth));
      line = '';
    } else {
      line = word;
    }
  }

  if (line) {
    lines.push(line);
  }
  return lines.length ? lines : ['Untitled audio'];
}

function breakLongWord(context: CanvasRenderingContext2D, word: string, maxWidth: number) {
  const chunks: string[] = [];
  let chunk = '';
  for (const char of word) {
    const candidate = chunk + char;
    if (context.measureText(candidate).width > maxWidth && chunk) {
      chunks.push(chunk);
      chunk = char;
    } else {
      chunk = candidate;
    }
  }
  if (chunk) {
    chunks.push(chunk);
  }
  return chunks;
}

function drawLines(context: CanvasRenderingContext2D, lines: string[], x: number, y: number, lineHeight: number) {
  lines.forEach((line, index) => {
    context.fillText(line, x, y + index * lineHeight);
  });
}

function drawLinesCentered(context: CanvasRenderingContext2D, lines: string[], x: number, y: number, lineHeight: number) {
  lines.forEach((line, index) => {
    context.fillText(line, x - context.measureText(line).width / 2, y + index * lineHeight);
  });
}

function drawRoundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  fill: string,
) {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
  context.fillStyle = fill;
  context.fill();
}

function mixColors(hexA: string, hexB: string, weight: number) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  const mixed = a.map((channel, index) => Math.round(channel * (1 - weight) + b[index] * weight));
  return `rgb(${mixed[0]}, ${mixed[1]}, ${mixed[2]})`;
}

function colorWithAlpha(hex: string, alpha: number) {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function hexToRgb(hex: string) {
  const value = hex.replace('#', '');
  return [0, 2, 4].map((index) => Number.parseInt(value.slice(index, index + 2), 16));
}
